/**
 * Deterministic B2B segment classification (explicit before inferred).
 * Shared by partner attribution snapshots and pipeline reporting.
 */

import { isB2bEventType } from '../config/b2bEventTypes';

export type B2bSegment =
  | 'foretagsevent'
  | 'foretagsfest'
  | 'julfest'
  | 'konferens'
  | 'massa'
  | 'kickoff'
  | 'invigning_jubileum'
  | 'eventproduktion'
  | 'ljud_av_foretag'
  | 'ljus'
  | 'scen_tross_rigg'
  | 'dj_corporate'
  | 'other_b2b'
  | 'unknown';

export type B2bClassificationMethod = 'explicit' | 'inferred' | 'unknown';

export type B2bClassification = {
  segment: B2bSegment;
  method: B2bClassificationMethod;
  evidence: string[];
};

const EVENT_TYPE_TO_SEGMENT: Record<string, B2bSegment> = {
  konferens: 'konferens',
  företagsfest: 'foretagsfest',
  foretagsfest: 'foretagsfest',
  kickoff: 'kickoff',
  gala: 'other_b2b',
  produktlansering: 'other_b2b',
  mässa: 'massa',
  massa: 'massa',
  julfest: 'julfest',
  seminarium: 'konferens',
  företagsevent: 'foretagsevent',
  foretagsevent: 'foretagsevent',
  invigning: 'invigning_jubileum',
  jubileum: 'invigning_jubileum',
  personalfest: 'foretagsfest',
  annat: 'other_b2b',
};

const HUB_NEED_TO_SEGMENT: Record<string, B2bSegment> = {
  foretagsevent: 'foretagsevent',
  konferens: 'konferens',
  massa: 'massa',
};

function norm(s: string | null | undefined): string {
  return String(s ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .trim();
}

/**
 * Deterministic B2B segment. Explicit (form event_type / hub_need) before inference.
 */
export function classifyB2bSegment(input: {
  event_type?: string | null;
  hub_need_card?: string | null;
  cta_context?: string | null;
  utm_campaign?: string | null;
  source_page?: string | null;
  skaneevent_path?: string | null;
  landing_page?: string | null;
  form_variant?: string | null;
  package_type?: string | null;
}): B2bClassification {
  const evidence: string[] = [];

  const hub =
    input.hub_need_card ||
    (input.cta_context?.startsWith('foretag_need_')
      ? input.cta_context.slice('foretag_need_'.length)
      : null);
  if (hub && HUB_NEED_TO_SEGMENT[hub]) {
    evidence.push(`hub_need_card:${hub}`);
    return { segment: HUB_NEED_TO_SEGMENT[hub], method: 'explicit', evidence };
  }

  const et = norm(input.event_type);
  if (et && EVENT_TYPE_TO_SEGMENT[et]) {
    evidence.push(`event_type:${input.event_type}`);
    return { segment: EVENT_TYPE_TO_SEGMENT[et], method: 'explicit', evidence };
  }
  if (et && isB2bEventType(String(input.event_type))) {
    evidence.push(`event_type_other:${input.event_type}`);
    return { segment: 'other_b2b', method: 'explicit', evidence };
  }

  const campaign = norm(input.utm_campaign);
  if (campaign && EVENT_TYPE_TO_SEGMENT[campaign]) {
    evidence.push(`utm_campaign:${input.utm_campaign}`);
    return { segment: EVENT_TYPE_TO_SEGMENT[campaign], method: 'inferred', evidence };
  }

  const blob = [
    norm(input.skaneevent_path),
    norm(input.source_page),
    norm(input.landing_page),
    norm(input.package_type),
    norm(input.form_variant),
  ].join(' ');

  const inferredRules: Array<{ re: RegExp; segment: B2bSegment; label: string }> = [
    { re: /julfest/, segment: 'julfest', label: 'path:julfest' },
    { re: /kickoff|kick-off|kick off/, segment: 'kickoff', label: 'path:kickoff' },
    { re: /massa|massor|monter/, segment: 'massa', label: 'path:massa' },
    { re: /konferens/, segment: 'konferens', label: 'path:konferens' },
    { re: /invigning|jubileum/, segment: 'invigning_jubileum', label: 'path:invigning' },
    { re: /eventproduktion|produktion/, segment: 'eventproduktion', label: 'path:produktion' },
    { re: /foretagsfest|foretags-fest/, segment: 'foretagsfest', label: 'path:foretagsfest' },
    { re: /foretagsevent|\/foretag/, segment: 'foretagsevent', label: 'path:foretag' },
    { re: /eventteknik/, segment: 'other_b2b', label: 'path:eventteknik' },
    { re: /\bdj\b/, segment: 'dj_corporate', label: 'path:dj' },
    { re: /scen|tross|rigg/, segment: 'scen_tross_rigg', label: 'path:scen' },
    { re: /ljus|moving.?head|led/, segment: 'ljus', label: 'path:ljus' },
    { re: /ljud|hogtalare|mikrofon|av\b/, segment: 'ljud_av_foretag', label: 'path:ljud' },
  ];

  for (const rule of inferredRules) {
    if (rule.re.test(blob)) {
      evidence.push(rule.label);
      return { segment: rule.segment, method: 'inferred', evidence };
    }
  }

  if (
    input.form_variant === 'b2b_event' ||
    /skane event|foretag/i.test(String(input.package_type ?? ''))
  ) {
    evidence.push('form_variant_or_package');
    return { segment: 'other_b2b', method: 'inferred', evidence };
  }

  return { segment: 'unknown', method: 'unknown', evidence };
}
